import React, { useEffect, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import { X, ZoomIn, ZoomOut } from "lucide-react";

const fmtTime = (s) => (s ? new Date(s).toLocaleString() : "—");
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export const ReceiptModal = ({ expense, onClose, onReimbursement, canManage }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rStatus, setRStatus] = useState(expense.reimbursement_status || "pending");

  useEffect(() => {
    let url;
    const load = async () => {
      if (!expense.receipt_storage_path) return;
      try {
        const token = getToken();
        const res = await fetch(`${API}/expenses/${expense.id}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { url = URL.createObjectURL(await res.blob()); setBlobUrl(url); }
      } catch { /* ignore */ }
    };
    load();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [expense]);

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-3" data-testid="receipt-modal">
      <div className="bg-white border border-stone-200 shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-stone-200 sticky top-0 bg-white z-10">
          <h3 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Receipt · {expense.vendor || "—"}</h3>
          <button data-testid="receipt-close" onClick={onClose} className="h-9 w-9 flex items-center justify-center border border-stone-200"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="bg-stone-900 p-3 flex flex-col items-center">
            <div className="flex gap-2 mb-2 self-end">
              <button onClick={() => setZoom((z) => Math.max(1, z - 0.25))} className="h-8 w-8 bg-white/10 text-white flex items-center justify-center"><ZoomOut size={16} /></button>
              <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="h-8 w-8 bg-white/10 text-white flex items-center justify-center"><ZoomIn size={16} /></button>
            </div>
            <div className="overflow-auto w-full max-h-[70vh]">
              {blobUrl ? (
                <img src={blobUrl} alt="Receipt" data-testid="receipt-image" style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }} className="mx-auto transition-transform" />
              ) : (
                <div className="text-white/60 text-sm p-12 text-center">No receipt image attached.</div>
              )}
            </div>
          </div>
          <div className="p-5 space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {[["Date", expense.receipt_date], ["Vendor", expense.vendor], ["Amount", money(expense.amount)], ["Card", expense.card_last4 ? `••${expense.card_last4}` : "—"], ["Employee", expense.employee_name], ["Ranch", expense.ranch_name], ["Job Category", expense.job_category], ["Job Code", expense.job_code || "—"], ["Block", expense.block || "—"], ["Expense Type", expense.expense_category]].map(([k, v]) => (
                <div key={k}><div className="text-[10px] uppercase tracking-wide text-stone-400 font-bold">{k}</div><div className="font-medium">{v || "—"}</div></div>
              ))}
            </div>
            {expense.notes && <div><div className="text-[10px] uppercase tracking-wide text-stone-400 font-bold">Employee Notes</div><div>{expense.notes}</div></div>}

            {expense.needs_reimbursement && (
              <div className="border border-[#D8C7A1] bg-[#D8C7A1]/30 p-3">
                <div className="text-[10px] uppercase tracking-wide font-bold mb-1">Reimbursement ({expense.reimbursement_method || "—"})</div>
                {canManage ? (
                  <div className="flex items-center gap-2">
                    <select data-testid="reimb-status-select" value={rStatus} onChange={(e) => setRStatus(e.target.value)} className="h-9 px-2 border border-stone-300 bg-white text-xs">
                      {["pending", "approved", "paid"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button data-testid="reimb-save" onClick={() => onReimbursement(expense.id, rStatus)} className="h-9 px-3 bg-[#1F3D34] text-white font-bold uppercase text-xs">Save</button>
                  </div>
                ) : (<div className="font-bold uppercase text-xs">{expense.reimbursement_status}</div>)}
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-wide text-stone-400 font-bold mb-1">Audit History</div>
              <div className="space-y-1">
                {(expense.audit || []).map((a, i) => (
                  <div key={i} className="flex justify-between text-xs border-b border-stone-100 py-1">
                    <span className="font-bold uppercase">{a.action}{a.note ? ` — ${a.note}` : ""}</span>
                    <span className="text-stone-500">{a.by_name} · {fmtTime(a.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
